import {
  FeedbackMode,
  SectionTypeEnum,
  type TestGroupingGroup,
  type TestGroupingItem,
} from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import cn from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { useContext } from 'use-context-selector'

import { getObjectFitDivMap } from 'components/Editor/EditorElements/utils/getObjectFitDivMap'
import { RichTextViewer } from 'components/form/RichText/RichTextViewer'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getSection } from 'services/Store/Project/selectors'
import { useUrlByUsage } from 'utils/files'

import TestGroupingContext from '../../TestGroupingContext'
import IconLeft from '../../assets/icons-for-elements-left.svg?react'
import IconRight from '../../assets/icons-for-elements-right.svg?react'
import { borderRadiusContent } from '../../helper'
import FeedbackIcon from '../FeedbackIcon'
import * as s from './MobileGroupStack.module.scss'

const BASE_OFFSET = 16
const RESULT_OFFSET = 20
const MAX_VISIBLE_CARDS = 4

type CarouselItem =
  | { type: 'group'; data: TestGroupingGroup }
  | { type: 'answer'; data: TestGroupingItem }

interface IMobileGroupStackProps {
  carouselItems: CarouselItem[]
  stackIndex: number
  cardSize: { width?: number | string; height?: number | string }
  handleStackPrevious: (e: React.MouseEvent) => void
  handleStackNext: (e: React.MouseEvent) => void
}

const MobileGroupStack = ({
  carouselItems,
  stackIndex,
  cardSize,
  handleStackPrevious,
  handleStackNext,
}: IMobileGroupStackProps) => {
  const { styles } = useContext(TestGroupingContext)
  const totalItems = carouselItems.length

  return (
    <div style={cardSize}>
      <div className={s.stackContainer} style={cardSize}>
        <AnimatePresence initial={false}>
          {carouselItems.map((carouselItem, index) => {
            const relativePosition = index - stackIndex
            const isActiveInCarousel = index === stackIndex
            const key =
              carouselItem.type === 'group' ? carouselItem.data.value : carouselItem.data.value

            return (
              <MobileStackCard
                carouselItem={carouselItem}
                isActive={isActiveInCarousel}
                key={key}
                relativePosition={relativePosition}
              />
            )
          })}
        </AnimatePresence>
      </div>

      {totalItems > 1 && (
        <>
          <div
            className={cn(s.navButton, s.prev)}
            onClick={handleStackPrevious}
            style={{ color: styles.common.color }}
          >
            <IconLeft />
          </div>
          <div
            className={cn(s.navButton, s.next)}
            onClick={handleStackNext}
            style={{ color: styles.common.color }}
          >
            <IconRight />
          </div>
        </>
      )}
    </div>
  )
}

interface IMobileStackCardProps {
  carouselItem: CarouselItem
  relativePosition: number
  isActive: boolean
}

const MobileStackCard = ({ carouselItem, relativePosition, isActive }: IMobileStackCardProps) => {
  const { styles, font, testResult, blockResult, feedback, value } = useContext(TestGroupingContext)
  const section = useProjectContext(getSection)
  const feedbackMode = section?.test?.feedbackMode
  const showFbIcon =
    section?.type === SectionTypeEnum.landing ||
    (section?.type === SectionTypeEnum.test &&
      feedbackMode !== FeedbackMode.HIDE_ALL &&
      feedbackMode !== FeedbackMode.HIDE_ICON)
  const isGroup = carouselItem.type === 'group'

  const imageData = isGroup
    ? (carouselItem.data as TestGroupingGroup).target.image
    : (carouselItem.data as TestGroupingItem).image
  const label = isGroup
    ? (carouselItem.data as TestGroupingGroup).target.label
    : (carouselItem.data as TestGroupingItem).label
  const overlay = isGroup
    ? (carouselItem.data as TestGroupingGroup).target.overlay
    : (carouselItem.data as TestGroupingItem).overlay

  const imageUrl = useUrlByUsage(imageData, FileUsageTypeEnum.image)
  const hasImage = !!imageData?.id
  const backgroundImage = (hasImage && `url(${imageUrl})`) || undefined

  const itemStyles = isGroup ? styles.group : styles.answer
  const borderRadius = borderRadiusContent(itemStyles) || styles.common.borderRadius

  const absPosition = Math.abs(relativePosition)
  const isVisible = absPosition < MAX_VISIBLE_CARDS

  const visiblePosition = Math.min(absPosition, MAX_VISIBLE_CARDS - 1)
  const baseOffset = blockResult ? RESULT_OFFSET : BASE_OFFSET
  const stackOffset = visiblePosition * baseOffset

  const getStackValue = (base: number, factor: number, min: number) => {
    if (isActive) {
      return base
    }
    if (isVisible) {
      return Math.max(base - absPosition * factor, min)
    }
    return min
  }

  const scale = getStackValue(1, 0.08, 0.7)
  const opacity = getStackValue(1, 0.25, 0)
  const zIndex = isActive ? 100 : 100 - absPosition

  const cardStyles = { ...itemStyles, ...styles.common }
  const groupValue = isGroup ? (carouselItem.data as TestGroupingGroup).value : null

  return (
    <div className={s.stackCard}>
      <motion.div
        animate={{
          y: -stackOffset,
          scale: scale,
          opacity: opacity,
        }}
        className={s.stackItem}
        id={isGroup ? groupValue || undefined : undefined}
        initial={{
          scale: 0,
          opacity: 0,
          y: 0,
        }}
        style={{
          ...cardStyles,
          backgroundImage,
          backgroundSize: getObjectFitDivMap(imageData?.objectFit),
          zIndex,
          backgroundColor: itemStyles.backgroundColor,
          width: styles.common.width,
          height: styles.common.height,
        }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30,
          mass: 0.6,
        }}
        whileHover={
          relativePosition !== 0
            ? {
                y: -stackOffset - 4,
              }
            : undefined
        }
      >
        <div
          className={s.itemMask}
          id={isGroup ? groupValue || undefined : undefined}
          style={{
            borderRadius,
            backgroundColor: overlay || '',
          }}
        />
        <div
          className={s.itemLabel}
          id={isGroup ? groupValue || undefined : undefined}
          style={{
            justifyContent: itemStyles.alignItems,
          }}
        >
          <div
            className={s.textInner}
            id={isGroup ? groupValue || undefined : undefined}
            style={font?.base}
          >
            <RichTextViewer styles={font} value={label} />
          </div>
        </div>
        {!isGroup && blockResult && value.answerFeedback && feedback && showFbIcon && (
          <FeedbackIcon
            feedback={feedback}
            isTrueAnswer={testResult[(carouselItem.data as TestGroupingItem).value]}
          />
        )}
      </motion.div>
    </div>
  )
}
export default MobileGroupStack
